import {
  getAdClickIds,
  getLandingPageUrl,
  getVisitorId,
} from "~/lib/helpers/adClickIds";
import { MiscellaneousFormHandler } from "~/lib/helpers/regionPardotFormHelper";
import { getJourneySummaryString } from "~/lib/helpers/userJourney";
import { getTrafficSourceString } from "~/lib/helpers/trafficSource";

/**
 * Associates the visitor with Pardot tracking cookie via hidden iframe.
 */
export function createPardotAssociation(data: Record<string, string>) {
  if (!data) return;

  const encodedData = new URLSearchParams(data).toString();
  const iframeEl = document.createElement("iframe");

  iframeEl.setAttribute("src", MiscellaneousFormHandler() + "?" + encodedData);
  iframeEl.setAttribute("width", "1");
  iframeEl.setAttribute("height", "1");
  iframeEl.style.display = "none";

  document.body.appendChild(iframeEl);
}

/**
 * Submits a specific Pardot form handler by creating a hidden iframe.
 * Payload should be an object with keys reflecting the form handler's fields.
 */
export function submitPardotFormHandlerAsIframe(
  endpoint: string,
  payload: Record<string, string>
) {
  const encodedData = new URLSearchParams(payload).toString();
  const iframeEl = document.createElement("iframe");

  iframeEl.setAttribute("src", endpoint + "?" + encodedData);
  iframeEl.setAttribute("width", "1");
  iframeEl.setAttribute("height", "1");
  iframeEl.style.display = "none";

  document.body.appendChild(iframeEl);
}

/**
 * Submits a specific Pardot form handler using fetch (instead of JSONP).
 * On success → also creates a Pardot association (iframe).
 * On error → falls back to iframe submission.
 * Ad click IDs (msclkid/gclid/gbraid/wbraid) are added; call-site values win.
 */
export async function submitPardotFormHandler(
  endpoint: string,
  formPayload: Record<string, string>,
  errCallback?: () => void,
  succCallback?: () => void
) {
  // Annotated: spreading a Record<string, string> does not carry its index
  // signature onto the result, so the literal type would reject userLandingUrl.
  const payload: Record<string, string> = { ...getAdClickIds(), ...formPayload };
  if (!payload.userLandingUrl) {
    payload.userLandingUrl = getLandingPageUrl();
  }
  // Empty when functional cookies were refused; Pardot drops empty values.
  if (!payload.visitor_id) {
    payload.visitor_id = getVisitorId();
  }
  // One field rather than six, so only one Pardot field has to be created.
  if (!payload.journey_summary) {
    payload.journey_summary = getJourneySummaryString();
  }
  // e.g. "bing/paid_search" — one field, the shape marketers already read.
  if (!payload.traffic_source) {
    payload.traffic_source = getTrafficSourceString();
  }
  try {
    await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams(payload).toString(),
      mode: "no-cors", // Pardot won't return proper CORS headers
    });

    // Even though no-cors makes response opaque, we assume success if no network error
    createPardotAssociation(payload);
    if (succCallback) succCallback();
  } catch (err) {
    console.error("submitPardotFormHandler fetch error", err);

    // fallback: use iframe method
    submitPardotFormHandlerAsIframe(endpoint, payload);

    if (errCallback) errCallback();
  }
}
